import { SurfaceDefectChip } from "@/shared/components/SurfaceDefectChip";
import type { InspectionTemplateColumn } from "@/shared/models/Inspection";
import { backgroundColor } from "@/utils/theme";
import { dxtToLocalServerTime } from "@dexteel/mesf-core";
import {
	Check,
	Close,
	DeleteOutline,
	EditOutlined,
	ExpandLess,
	ExpandMore,
} from "@mui/icons-material";
import {
	Box,
	Collapse,
	IconButton,
	TableCell,
	TableRow,
	Tooltip,
	Typography,
} from "@mui/material";
import { type KeyboardEvent, type ReactNode, useRef, useState } from "react";
import { InspectionReading } from "../../models/Inspection";
import {
	CellDraft,
	DefectDraft,
	EMPTY_CELL,
	formatCellValue,
	isCellEmpty,
	readCellValue,
	ReadingDraft,
} from "../../models/Reading";
import {
	DateTimeCell,
	HeatCell,
	LengthCell,
	NumberCell,
	PassFailCell,
	PassFailMark,
	passFailSx,
	TextCell,
	TimestampCell,
} from "./ReadingCells";
import { SurfaceDefectsPanel } from "./SurfaceDefectsPanel";
import { EDITORS, focusEditor, navigationStep, stepEditor } from "./cellFocus";
import { textWidth } from "./textWidth";

const centerSx = { textAlign: "center" } as const;

export const ACTIONS_CLASS = "sheet-actions";

const actionsSx = {
	"& > *": { display: "inline-flex", verticalAlign: "middle" },
	textAlign: "center",
	whiteSpace: "nowrap",
	backgroundColor: "#fff",
} as const;

const ActionButton = ({
	title,
	color,
	disabled = false,
	onClick,
	children,
}: {
	title: string;
	color?: "primary" | "error";
	disabled?: boolean;
	onClick?: () => void;
	children: ReactNode;
}) => (
	<Tooltip title={title}>
		<span>
			<IconButton
				size="small"
				color={color}
				sx={{ p: 0.25 }}
				disabled={disabled}
				onClick={onClick}
			>
				{children}
			</IconButton>
		</span>
	</Tooltip>
);

const DEFECTS_TYPE = { size: 11, weight: 600 } as const;

const chipsThatFit = (names: string[], width: number) => {
	let used = 0;

	for (let shown = 0; shown < names.length; shown++) {
		used += textWidth(names[shown], DEFECTS_TYPE) + 20;

		const rest = names.length - shown - 1;
		const overflow = rest === 0 ? 0 : textWidth(`+${rest}`, DEFECTS_TYPE) + 4;

		if (used + overflow > width) return shown;
	}

	return names.length;
};

const DefectsCount = ({
	defects,
	open,
	onToggle,
}: {
	defects: { SurfaceDefectName: string | null }[];
	open: boolean;
	onToggle: (() => void) | null;
}) => {
	const names = defects.map((defect) => defect.SurfaceDefectName || "?");

	return (
		<TableCell sx={centerSx}>
			<Box
				onClick={onToggle ?? undefined}
				title={names.length > 0 ? names.join(", ") : "No defects"}
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					cursor: onToggle ? "pointer" : "default",
				}}
			>
				<Typography
					sx={{
						fontSize: DEFECTS_TYPE.size,
						fontWeight: names.length > 0 ? 700 : 400,
					}}
					color={names.length > 0 ? "#ed6c02" : "text.disabled"}
				>
					{names.length === 0 ? "-" : names.length}
				</Typography>
				{onToggle &&
					(open ? (
						<ExpandLess sx={{ fontSize: 14, color: "action.active" }} />
					) : (
						<ExpandMore sx={{ fontSize: 14, color: "action.active" }} />
					))}
			</Box>
		</TableCell>
	);
};

const DefectsCell = ({
	defects,
	width,
	open,
	onToggle,
}: {
	defects: {
		SurfaceDefectName: string | null;
		Color: string | null;
	}[];
	width: number;
	open: boolean;
	onToggle: (() => void) | null;
}) => {
	const names = defects.map((defect) => defect.SurfaceDefectName || "?");
	const room = Math.max(0, width - 8 - (onToggle ? 16 : 0));
	const shown = chipsThatFit(names, room);
	const hidden = names.length - shown;

	return (
		<TableCell>
			<Box
				onClick={onToggle ?? undefined}
				title={names.length > 0 ? names.join(", ") : undefined}
				sx={{
					display: "flex",
					alignItems: "center",
					gap: 0.5,
					cursor: onToggle ? "pointer" : "default",
					minWidth: 0,
				}}
			>
				{names.length === 0 ? (
					<Typography
						sx={{ fontSize: DEFECTS_TYPE.size }}
						color="text.disabled"
					>
						No defects
					</Typography>
				) : (
					<>
						{defects.slice(0, Math.max(shown, 1)).map((defect, index) => (
							<SurfaceDefectChip
								key={`${defect.SurfaceDefectName ?? "?"}-${index}`}
								name={defect.SurfaceDefectName || "?"}
								color={defect.Color}
							/>
						))}
						{hidden > 0 && (
							<Typography
								sx={{ fontSize: DEFECTS_TYPE.size, fontWeight: 600 }}
								color="text.secondary"
							>
								{`+${hidden}`}
							</Typography>
						)}
					</>
				)}
				{onToggle &&
					(open ? (
						<ExpandLess sx={{ fontSize: 14, color: "action.active" }} />
					) : (
						<ExpandMore sx={{ fontSize: 14, color: "action.active" }} />
					))}
			</Box>
		</TableCell>
	);
};

const DefectsRow = ({
	open,
	columnCount,
	onKeyDown,
	children,
}: {
	open: boolean;
	columnCount: number;
	onKeyDown?: (event: KeyboardEvent<HTMLTableRowElement>) => void;
	children: ReactNode;
}) => (
	<TableRow onKeyDown={onKeyDown}>
		<TableCell
			colSpan={columnCount}
			sx={{
				p: 0,
				height: "auto !important",
				borderRight: "none !important",
				borderBottom: open ? undefined : "none",
				overflow: "visible",
				whiteSpace: "normal",
			}}
		>
			<Collapse in={open} timeout="auto" unmountOnExit>
				{children}
			</Collapse>
		</TableCell>
	</TableRow>
);

type DisplayProps = {
	index: number;
	reading: InspectionReading;
	columns: InspectionTemplateColumn[];
	columnCount: number;
	defectsWidth: number;
	compactDefects?: boolean;
	isLastUpdated?: boolean;
	disabled: boolean;
	showActions?: boolean;
	onEdit?: () => void;
	onDelete?: () => void;
};

export const DisplayReadingRow = ({
	index,
	reading,
	columns,
	columnCount,
	defectsWidth,
	compactDefects = false,
	isLastUpdated = false,
	disabled,
	showActions = true,
	onEdit,
	onDelete,
}: DisplayProps) => {
	const [open, setOpen] = useState(false);
	const hasDefects = reading.Defects.length > 0;

	return (
		<>
			<TableRow
				sx={{
					"&:hover": { backgroundColor },
					[`&:hover .${ACTIONS_CLASS}`]: { backgroundColor },
					...(isLastUpdated
						? {
								backgroundColor: "#fff4e5",
								[`& .${ACTIONS_CLASS}`]: {
									backgroundColor: "#fff4e5",
								},
							}
						: null),
				}}
			>
				<TableCell sx={{ ...centerSx, color: "text.disabled" }}>
					{index}
				</TableCell>
				<TableCell sx={centerSx}>
					{dxtToLocalServerTime(reading.RowTimestamp, "MM/dd HH:mm")}
				</TableCell>
				<TableCell
					sx={{
						...centerSx,
						...(reading.HeatName ? null : { color: "text.disabled" }),
					}}
				>
					{reading.HeatName || "-"}
				</TableCell>

				{columns.map((column) => {
					const columnId = column.ColumnId as number;
					const raw = readCellValue(column, reading.ValueByColumnId[columnId]);
					const empty = isCellEmpty(reading.ValueByColumnId[columnId]);
					const isPassFail = column.ValueTypeCode === "G" && !empty;

					const text = formatCellValue(column, raw);

					return (
						<TableCell
							key={columnId}
							title={text || undefined}
							sx={{
								...centerSx,
								...(empty ? { color: "text.disabled" } : null),
								...(isPassFail ? passFailSx(raw as boolean) : null),
								fontVariantNumeric: "tabular-nums",
							}}
						>
							{isPassFail ? (
								<PassFailMark value={raw as boolean} />
							) : (
								text || "-"
							)}
						</TableCell>
					);
				})}

				{compactDefects ? (
					<DefectsCount
						defects={reading.Defects}
						open={open}
						onToggle={hasDefects ? () => setOpen(!open) : null}
					/>
				) : (
					<DefectsCell
						defects={reading.Defects}
						width={defectsWidth}
						open={open}
						onToggle={hasDefects ? () => setOpen(!open) : null}
					/>
				)}

				{showActions && (
					<TableCell className={ACTIONS_CLASS} sx={actionsSx}>
						<ActionButton
							title="Edit reading"
							disabled={disabled}
							onClick={onEdit}
						>
							<EditOutlined fontSize="small" />
						</ActionButton>
						<ActionButton
							title="Delete reading"
							disabled={disabled}
							onClick={onDelete}
						>
							<DeleteOutline fontSize="small" />
						</ActionButton>
					</TableCell>
				)}
			</TableRow>

			{hasDefects && (
				<DefectsRow open={open} columnCount={columnCount}>
					<SurfaceDefectsPanel
						selected={reading.Defects.map((defect) => ({
							SurfaceDefectId: defect.SurfaceDefectId,
							SurfaceDefectName: defect.SurfaceDefectName ?? "",
							Color: defect.Color,
							Comments: defect.Comments,
							Path: defect.Path,
						}))}
					/>
				</DefectsRow>
			)}
		</>
	);
};

type EditProps = {
	index: number;
	draft: ReadingDraft;
	columns: InspectionTemplateColumn[];
	sectionId: number | null;
	columnCount: number;
	defectsWidth: number;
	isMutating: boolean;
	onChange: (draft: ReadingDraft) => void;
	onSave: () => void;
	onCancel: () => void;
};

export const EditReadingRow = ({
	index,
	draft,
	columns,
	sectionId,
	columnCount,
	defectsWidth,
	isMutating,
	onChange,
	onSave,
	onCancel,
}: EditProps) => {
	const [open, setOpen] = useState(draft.Defects.length > 0);
	const rowRef = useRef<HTMLTableRowElement>(null);
	const defectsRef = useRef<HTMLDivElement>(null);

	const canSave = draft.RowTimestamp !== null && !isMutating;

	const setCell = (columnId: number, cell: Partial<CellDraft>) =>
		onChange({
			...draft,
			Cells: {
				...draft.Cells,
				[columnId]: { ...(draft.Cells[columnId] ?? EMPTY_CELL), ...cell },
			},
		});

	const focusDefects = () => {
		const firstEditor = () =>
			defectsRef.current?.querySelector<HTMLElement>(EDITORS);

		if (open) return focusEditor(firstEditor());

		setOpen(true);
		requestAnimationFrame(() => focusEditor(firstEditor()));

		return true;
	};

	const focusLastValue = () => {
		const editors = rowRef.current?.querySelectorAll<HTMLElement>(EDITORS);

		return focusEditor(editors && editors[editors.length - 1]);
	};

	const move = (target: HTMLElement, step: -1 | 1) => {
		if (stepEditor(rowRef.current, target, step)) return true;

		const atLastValue =
			step === 1 &&
			rowRef.current?.contains(target) === true &&
			target.matches(EDITORS);

		return atLastValue && focusDefects();
	};

	const onRowKeyDownCapture = (event: KeyboardEvent<HTMLTableRowElement>) => {
		if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;

		const step = navigationStep(event);

		if (step !== null && move(event.target as HTMLElement, step))
			event.preventDefault();
	};

	const onRowKeyDown = (event: KeyboardEvent<HTMLTableRowElement>) => {
		if (event.defaultPrevented) return;

		const target = event.target as HTMLElement;

		if (event.key === "Escape") {
			event.preventDefault();
			onCancel();
			return;
		}

		if (event.key === "Enter") {
			if (event.ctrlKey || event.metaKey) {
				event.preventDefault();
				if (canSave) onSave();
				return;
			}

			if (move(target, 1)) event.preventDefault();
			return;
		}

		const step = navigationStep(event);

		if (step !== null && move(target, step)) event.preventDefault();
	};

	return (
		<>
			<TableRow
				ref={rowRef}
				onKeyDownCapture={onRowKeyDownCapture}
				onKeyDown={onRowKeyDown}
				sx={(theme) => ({
					backgroundColor,
					"& td:not(:last-child):focus-within": {
						outline: `2px solid ${theme.palette.primary.main}`,
						outlineOffset: "-2px",
					},
				})}
			>
				<TableCell sx={{ ...centerSx, color: "text.disabled" }}>
					{index}
				</TableCell>

				<TableCell>
					<TimestampCell
						value={draft.RowTimestamp}
						onChange={(value) => onChange({ ...draft, RowTimestamp: value })}
					/>
				</TableCell>

				<TableCell>
					<HeatCell
						value={draft.HeatId}
						sectionId={sectionId}
						onChange={(value) => onChange({ ...draft, HeatId: value })}
					/>
				</TableCell>

				{columns.map((column) => {
					const columnId = column.ColumnId as number;
					const cell = draft.Cells[columnId] ?? EMPTY_CELL;

					return (
						<TableCell key={columnId}>
							{column.ValueTypeCode === "G" ? (
								<PassFailCell
									value={cell.PassFail}
									onChange={(value) => setCell(columnId, { PassFail: value })}
								/>
							) : column.ValueTypeCode === "D" ? (
								<DateTimeCell
									value={cell.DateTimeValue}
									onChange={(value) =>
										setCell(columnId, { DateTimeValue: value })
									}
								/>
							) : column.ValueTypeCode === "L" ? (
								<LengthCell
									value={cell.NumericValue}
									onChange={(value) =>
										setCell(columnId, { NumericValue: value })
									}
								/>
							) : column.ValueTypeCode === "N" ? (
								<NumberCell
									value={cell.NumericValue}
									onChange={(value) =>
										setCell(columnId, { NumericValue: value })
									}
								/>
							) : (
								<TextCell
									value={cell.TextValue}
									onChange={(value) => setCell(columnId, { TextValue: value })}
								/>
							)}
						</TableCell>
					);
				})}

				<DefectsCell
					defects={draft.Defects}
					width={defectsWidth}
					open={open}
					onToggle={() => setOpen(!open)}
				/>

				<TableCell
					className={ACTIONS_CLASS}
					sx={{ ...actionsSx, backgroundColor }}
				>
					<ActionButton title="Discard changes (Esc)" onClick={onCancel}>
						<Close fontSize="small" />
					</ActionButton>
					<ActionButton
						title="Save reading (Ctrl + Enter)"
						color="primary"
						disabled={!canSave}
						onClick={onSave}
					>
						<Check fontSize="small" />
					</ActionButton>
				</TableCell>
			</TableRow>

			<DefectsRow
				open={open}
				columnCount={columnCount}
				onKeyDown={onRowKeyDown}
			>
				<Box ref={defectsRef} sx={{ backgroundColor }}>
					<SurfaceDefectsPanel
						selected={draft.Defects}
						onChange={(defects: DefectDraft[]) =>
							onChange({ ...draft, Defects: defects })
						}
						onLeave={focusLastValue}
					/>
				</Box>
			</DefectsRow>
		</>
	);
};
